/**
 * JVZOO-3448 — Whop express wallet button (Apple Pay / Google Pay above the Whop Payment Element).
 *
 * Built on a Whop Elements PaymentRequest. This module owns the wallet side only: the request's
 * lifecycle, the availability check, the buttons and the wallet sheet. It never charges.
 * jvzoo.checkout.whop.elements.js owns the charge: a wallet click hands a token getter to
 * JVZoo.Checkout.Whop.submitWithToken, which runs the same checks, duplicate check, payment
 * endpoint and polling as the card button.
 *
 * Bridge (jvzoo.checkout.whop.elements.js calls these):
 *   onSession(session)       after every successful session render
 *   onStateChange({canPay})  whenever the pay button's enabled state is recomputed
 *   onSessionCleared()       when the Whop session can't pay (switched away from Whop, or a quote failed)
 *
 * No-ops unless whop_express_button.twig rendered #whop-express-row.
 */
(function (window, document) {
    window.JVZoo = window.JVZoo || {};
    window.JVZoo.Checkout = window.JVZoo.Checkout || {};
    var checkout = window.JVZoo.Checkout;

    // Elements rendered by whop_express_button.twig and the order bump partials
    const ROW_SELECTOR = '#whop-express-row';
    const SLOT_SELECTOR = '#whop-express-slot';
    const BUMP_INPUT_ID_PREFIX = 'bumpOrder_';
    // order_bumps.twig names the bump in a label; above_the_fold_template/order_bumps.twig (ATF, Digital Flow) in the title.
    const BUMP_ITEM_SELECTOR = '.order_overview-item';
    const BUMP_TITLE_NAME_SELECTOR = '.order_bump-title > span:first-child';
    const DATA_PRODUCT_LABEL = 'data-product-label';
    const DATA_WALLET = 'data-wallet';
    // Note telling the buyer the wallet charges the product now and the checked bumps right after (the sheet total
    // shows the product only). Placed below the order bumps, or under the order total on pages without them.
    const BUMP_NOTE_ID = 'whop-express-bump-note';
    const BUMP_NOTE_CLASS = 'whop-express-bump-note';
    const ORDER_BUMPS_SELECTOR = '#order_bumps';
    const ORDER_TOTAL_SELECTOR = '[data-order-total]';
    // Keep synchronized with WHOP_EXPRESS_BUTTON_SELECTOR in jvzoo.checkout.whop.elements.js
    const BUTTON_CLASS = 'whop-express-wallet-button';
    const ROW_VISIBLE_CLASS = 'is-wallet-visible';

    // Wallets this button offers, keyed by Whop's wallet type, with the canMakePayment() flag for each.
    const WALLETS = {
        apple_pay: {availabilityKey: 'applePay', name: 'Apple Pay'},
        google_pay: {availabilityKey: 'googlePay', name: 'Google Pay'}
    };
    // Whop serves button art for light and dark pages; the checkout pages are light.
    const BUTTON_ART_THEME = 'light';
    const SETUP_FUTURE_USAGE = 'off_session';
    const DEFAULT_PRODUCT_LABEL = 'Order total';
    const BUMP_LINE_SUFFIX = ' (charged separately)';
    // Free trials save the wallet for the later charges and take nothing today; the page shows the recurring terms.
    const FREE_TRIAL_SUFFIX = ' (free trial)';
    // Same allowance the card form gets to become ready (ELEMENT_READY_TIMEOUT_MS in jvzoo.checkout.whop.elements.js).
    const WALLET_CHECK_TIMEOUT_MS = 10000;

    var _request = null;
    var _identity = null;
    var _generation = 0;
    var _canPay = false;
    // True while a replacement request is being created; the current buttons stay on screen, disabled.
    var _replacing = false;
    // Latest valid session from onSession, for the bump note.
    var _session = null;

    function row() { return document.querySelector(ROW_SELECTOR); }
    function slot() { return document.querySelector(SLOT_SELECTOR); }
    function decodeEntities(text) {
        // Product names can be stored HTML-encoded (e.g. "&amp;"); the wallet sheet shows plain text.
        try {
            return new DOMParser().parseFromString(text, 'text/html').documentElement.textContent || '';
        } catch (_) {
            return text;
        }
    }
    function productLabel() {
        var element = row();
        var label = element ? decodeEntities((element.getAttribute(DATA_PRODUCT_LABEL) || '').trim()).trim() : '';
        return label || DEFAULT_PRODUCT_LABEL;
    }
    function bumpLabel(id) {
        var input = document.getElementById(BUMP_INPUT_ID_PREFIX + id);
        if (!input) return '';
        var item = input.closest(BUMP_ITEM_SELECTOR);
        var name = (item && item.querySelector(BUMP_TITLE_NAME_SELECTOR))
            || document.querySelector('label[for="' + input.id + '"]');
        return name ? name.textContent.replace(/\s+/g, ' ').trim() : '';
    }
    function chargeAmount(session) {
        return session.isSetupMode ? 0 : session.amountMinor;
    }
    function lineItems(session, withBumps) {
        var items = [{label: productLabel() + (session.isSetupMode ? FREE_TRIAL_SUFFIX : ''), amount: chargeAmount(session)}];
        if (!withBumps) return items;
        session.bumps.forEach(function(bump) {
            var name = bumpLabel(bump.id);
            if (name && bump.amountMinor !== null) items.push({label: name + BUMP_LINE_SUFFIX, amount: bump.amountMinor});
        });
        return items;
    }
    function hasBumpLines(session) {
        return lineItems(session, true).length > 1;
    }
    function requestOptions(session, withBumps) {
        // The email comes from the order form, so the sheet does not ask for it.
        var options = {
            accountId: session.accountId,
            currency: session.currency,
            amount: chargeAmount(session),
            requestPayerEmail: false,
            lineItems: lineItems(session, withBumps)
        };
        if (session.requiresFutureUsage) options.setupFutureUsage = SETUP_FUTURE_USAGE;
        if (session.paymentMethodConfiguration) options.paymentMethodConfiguration = session.paymentMethodConfiguration;
        return options;
    }
    function sessionIdentity(session) {
        // A change to any of these needs a new request; amount and lines are updated in place.
        return [
            session.accountId,
            session.currency,
            session.isSetupMode,
            session.requiresFutureUsage,
            JSON.stringify(session.paymentMethodConfiguration || null)
        ].join('|');
    }
    function releaseRequest(request) {
        // The SDK names the teardown destroy() or dispose() depending on the build.
        if (!request) return;
        try {
            if (typeof request.destroy === 'function') request.destroy();
            else if (typeof request.dispose === 'function') request.dispose();
        } catch (_) {}
    }
    function destroyRequest() {
        releaseRequest(_request);
        _request = null;
        _identity = null;
    }
    function reportWallets(types, availableTypes) {
        // types: wallets on screen (the Payment Element offers Apple Pay itself when this row does not).
        // availableTypes: what Whop says the device can use; omitted when the row only hides after a failure, so a
        // later quote can bring the buttons back.
        if (typeof checkout.Whop.setExpressWallets === 'function') checkout.Whop.setExpressWallets(types, availableTypes);
    }
    function hideRow() {
        reportWallets([]);
        var element = row();
        if (!element) return;
        element.hidden = true;
        element.classList.remove(ROW_VISIBLE_CLASS);
        var container = slot();
        if (container) container.innerHTML = '';
        renderBumpNote();
    }
    function formatMoney(minor, currency) {
        try {
            return new Intl.NumberFormat('en-US', {style: 'currency', currency: String(currency).toUpperCase()})
                .format(minor / 100);
        } catch (_) {
            return '$' + (minor / 100).toFixed(2);
        }
    }
    function isVisible(element) { return !!element && element.offsetParent !== null; }
    function bumpNoteAnchor() {
        var bumps = document.querySelector(ORDER_BUMPS_SELECTOR);
        if (isVisible(bumps)) return bumps;
        var total = Array.prototype.filter.call(document.querySelectorAll(ORDER_TOTAL_SELECTOR), isVisible)[0];
        return total ? (total.closest('.price-row') || total.closest('h2') || total.parentElement) : null;
    }
    function renderBumpNote() {
        var note = document.getElementById(BUMP_NOTE_ID);
        var element = row();
        var bumps = _session && Array.isArray(_session.bumps) ? _session.bumps.filter(function(bump) {
            return bump.amountMinor !== null;
        }) : [];
        var anchor = bumpNoteAnchor();
        if (!element || element.hidden || !bumps.length || !anchor) {
            if (note) note.remove();
            return;
        }
        var addOns = bumps.reduce(function(sum, bump) { return sum + bump.amountMinor; }, 0);
        if (!note) {
            note = document.createElement('p');
            note.id = BUMP_NOTE_ID;
            note.className = BUMP_NOTE_CLASS;
        }
        if (note.previousElementSibling !== anchor) anchor.insertAdjacentElement('afterend', note);
        note.textContent = 'Apple Pay / Google Pay charges ' + formatMoney(chargeAmount(_session), _session.currency)
            + ' now. Your add-on' + (bumps.length === 1 ? '' : 's') + ' (' + formatMoney(addOns, _session.currency) + ') '
            + (bumps.length === 1 ? 'is' : 'are') + ' charged separately right after.';
    }
    function updateButtons() {
        var container = slot();
        if (!container) return;
        container.querySelectorAll('.' + BUTTON_CLASS).forEach(function(button) { button.disabled = !_canPay || _replacing; });
    }
    function createButton(type, availability) {
        var wallet = WALLETS[type];
        var button = document.createElement('button');
        button.type = 'button';
        button.className = BUTTON_CLASS + ' ' + BUTTON_CLASS + '--' + type;
        button.setAttribute(DATA_WALLET, type);
        button.setAttribute('aria-label', 'Pay with ' + wallet.name);
        var details = availability.wallets && availability.wallets[type];
        var art = details && details.button && details.button[BUTTON_ART_THEME];
        if (art && art.image) {
            if (art.color) button.style.backgroundColor = art.color;
            var image = document.createElement('img');
            image.src = art.image;
            image.alt = wallet.name;
            button.appendChild(image);
        } else {
            button.textContent = 'Pay with ' + wallet.name;
        }
        button.addEventListener('click', function() { pay(type); });
        return button;
    }
    function renderButtons(availability) {
        var element = row();
        var container = slot();
        if (!element || !container) return;
        var order = Array.isArray(availability.order) ? availability.order : Object.keys(WALLETS);
        var types = order.filter(function(type) {
            return WALLETS[type] && availability[WALLETS[type].availabilityKey] === true;
        });
        container.innerHTML = '';
        if (!types.length) {
            hideRow();
            reportWallets([], []);
            return;
        }
        types.forEach(function(type) { container.appendChild(createButton(type, availability)); });
        reportWallets(types, types);
        element.hidden = false;
        element.classList.add(ROW_VISIBLE_CLASS);
        updateButtons();
        renderBumpNote();
    }
    function createRequest(client, session, withBumps) {
        var request;
        try {
            request = client.payments.paymentRequest.create(requestOptions(session, withBumps));
        } catch (error) {
            // Options are validated at create; a refusal must reach the caller's fallback as a rejection.
            return Promise.reject(error);
        }
        if (typeof request.onPaymentAuthorization === 'function') {
            // Every handler must reply once. The server runs its checks after the token is created.
            request.onPaymentAuthorization(function(event) { event.respondWith({approved: true}); });
        }
        return withTimeout(request.canMakePayment(), WALLET_CHECK_TIMEOUT_MS).then(function(availability) {
            return {request: request, availability: availability || {}};
        }, function(error) {
            releaseRequest(request);
            throw error;
        });
    }
    function withTimeout(promise, ms) {
        // A wallet check that never settles would leave the row in its "checking" state for good, and Apple Pay
        // off inside the card form with it. A timeout counts as "no wallet" and the next quote checks again.
        return new Promise(function(resolve, reject) {
            var timer = setTimeout(function() { reject(new Error('Wallet check timed out')); }, ms);
            Promise.resolve(promise).then(function(value) {
                clearTimeout(timer);
                resolve(value);
            }, function(error) {
                clearTimeout(timer);
                reject(error);
            });
        });
    }
    function openRequest(session) {
        var generation = ++_generation;
        var identity = sessionIdentity(session);
        destroyRequest();
        // Buttons already on screen stay put, disabled, until the new request answers, so the row does not flicker.
        _replacing = true;
        updateButtons();
        checkout.Whop.getElementsClient().then(function(client) {
            if (!hasBumpLines(session)) return createRequest(client, session, false);
            // If the request with bump lines fails, retry this once without them so the buyer keeps the button; the
            // next request tries with the bump lines again.
            return createRequest(client, session, true).catch(function() {
                return createRequest(client, session, false);
            });
        }).then(function(created) {
            if (generation !== _generation) {
                releaseRequest(created.request);
                return;
            }
            _replacing = false;
            _request = created.request;
            _identity = identity;
            renderButtons(created.availability);
        }).catch(function() {
            if (generation !== _generation) return;
            _replacing = false;
            hideRow();
        });
    }
    function pay(type) {
        if (!_request || !_canPay || _replacing) return;
        var request = _request;
        var email = checkout.GetContactEmail ? (checkout.GetContactEmail() || '') : '';
        // Called synchronously from the click: submitWithToken calls this getter before awaiting
        // anything, so the wallet sheet opens inside the buyer's gesture.
        checkout.Whop.submitWithToken(function() {
            return request.show(type, email ? {email: email} : undefined).then(function(result) {
                if (!result || typeof result.ctok !== 'string') throw new Error('Invalid wallet token');
                return {confirmationToken: result.ctok};
            });
        });
    }

    // Without the Whop Elements checkout (legacy embed, or no Whop on the page) the express row stays hidden.
    if (!checkout.Whop || !checkout.Whop.isElementsCheckout || typeof checkout.Whop.submitWithToken !== 'function'
        || typeof checkout.Whop.getElementsClient !== 'function') {
        return;
    }

    checkout.WhopExpress = {
        onSession: function(session) {
            if (!row()) return;
            if (!session || !session.accountId || !session.currency || (!session.isSetupMode && !(session.amountMinor > 0))) {
                _session = null;
                _generation++;
                _replacing = false;
                destroyRequest();
                hideRow();
                return;
            }
            _session = session;
            renderBumpNote();
            if (_request && _identity === sessionIdentity(session) && typeof _request.update === 'function') {
                try {
                    var request = _request;
                    var updated = request.update({amount: chargeAmount(session), lineItems: lineItems(session, hasBumpLines(session))});
                    if (updated && typeof updated.then === 'function') {
                        updated.then(null, function() {
                            if (request !== _request) return;
                            openRequest(session);
                        });
                    }
                    return;
                } catch (_) {
                    // A refused update falls through to a new request.
                }
            }
            openRequest(session);
        },
        onStateChange: function(state) {
            _canPay = !!(state && state.canPay);
            updateButtons();
        },
        onSessionCleared: function() {
            _session = null;
            _generation++;
            _replacing = false;
            destroyRequest();
            hideRow();
        }
    };
})(window, document);
